// Reduced-VAT islands (ΑΑΔΕ Ε.2113/31-12-2025, ν.5144/2024 αρ.26 §4/4Α/4Β/5 as
// amended by ν.5246/2025 αρ.11) — in force from 1/1/2026. On these islands the
// standard rates drop: 24→17, 13→9, 6→4, 4→3.
//
// ⚠️ LEGAL GATE — this is DECISION LOGIC ONLY (pure, unit-tested). The reduction is
// NOT applied automatically by shipping postcode. It applies ONLY to a B2B sale
// shipped to a business ESTABLISHED on the island; retail sales to private
// individuals stay at 24% even when delivered there. Tobacco products and means of
// transport are excluded. `establishmentVerifiedOnIsland` must come from an explicit,
// verified signal (business attestation + review / admin), never inferred from the
// shipping address.

// The 24 main inhabited destinations + the administratively-attached islets ΑΑΔΕ lists.
export const REDUCED_VAT_ISLANDS = [
  // Βόρειο Αιγαίο
  'Άγιος Ευστράτιος', 'Ικαρία', 'Λέσβος', 'Λήμνος', 'Σάμος', 'Φούρνοι', 'Χίος', 'Ψαρά', 'Οινούσσες',
  // Δωδεκάνησα
  'Αγαθονήσι', 'Αστυπάλαια', 'Κάλυμνος', 'Κάρπαθος', 'Κάσος', 'Κως', 'Λειψοί', 'Λέρος',
  'Μεγίστη', 'Καστελλόριζο', 'Νίσυρος', 'Πάτμος', 'Σύμη', 'Τήλος', 'Χάλκη',
  // Έβρος
  'Σαμοθράκη',
  // Attached islets
  'Αρκοί', 'Ψέριμος', 'Τέλενδος', 'Θύμαινα', 'Σαμιοπούλα', 'Ρω', 'Κίναρος', 'Λέβιθα',
];

// Standard → reduced rate on these islands.
export const REDUCED_RATE_MAP = { 24: 17, 13: 9, 6: 4, 4: 3 };

export function reducedRate(standardRate) {
  const r = Number(standardRate);
  return Object.prototype.hasOwnProperty.call(REDUCED_RATE_MAP, r) ? REDUCED_RATE_MAP[r] : r;
}

// ΤΚ → island KEY — mirrors island-vat.php cn_island_zip_keys(). Whole island-only
// prefectures by 3-digit prefix; exacts where a prefix is shared. Ρόδος (851xx, 24%)
// is EXCLUDED — only Χάλκη 85110 / Καστελλόριζο 85111 qualify.
export const ISLAND_ZIP_PREFIX = {
  811: 'lesvos', 813: 'lesvos', 814: 'limnos', 815: 'ag-efstratios',
  821: 'chios', 822: 'chios', 823: 'chios',
  831: 'samos', 832: 'samos', 833: 'ikaria', 834: 'ikaria',
  852: 'kalymnos', 853: 'kos', 854: 'leros', 855: 'patmos',
  856: 'symi', 857: 'karpathos', 858: 'kasos', 859: 'astypalaia',
};
export const ISLAND_ZIP_EXACT = {
  68002: 'samothraki', 85001: 'lipsi-agathonisi', 85002: 'tilos', 85110: 'chalki', 85111: 'kastelorizo',
};

export function islandKeyForZip(zip) {
  const z = String(zip || '').replace(/\D/g, '').slice(0, 5);
  if (z.length < 5) return '';
  if (ISLAND_ZIP_EXACT[z]) return ISLAND_ZIP_EXACT[z];
  return ISLAND_ZIP_PREFIX[z.slice(0, 3)] || '';
}

export function isReducedVatPostcode(zip) {
  return islandKeyForZip(zip) !== '';
}

// Establishment + shipping ΤΚ on the SAME reduced island (goods shipped to the
// island of establishment) — the actual legal condition, not just "both islands".
export function sameReducedIsland(estZip, shipZip) {
  const k = islandKeyForZip(estZip);
  return k !== '' && k === islandKeyForZip(shipZip);
}

// Normalise a Greek place name (lowercase, strip accents/diacritics) for matching.
export function normalizeGreek(s) {
  return String(s || '')
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, ''); // combining accents
}

const ISLAND_SET = new Set(REDUCED_VAT_ISLANDS.map(normalizeGreek));

// Name-based membership (a helper for UI hints only — NOT a legal determination).
export function isReducedVatIsland(name) {
  return ISLAND_SET.has(normalizeGreek(name));
}

// The full legal gate. Returns true ONLY when EVERY condition holds. Any missing
// condition ⇒ standard 24% (the safe default).
//   islandDestination            — goods delivered to a reduced-VAT island
//   customerType                 — 'business' (υποκείμενος σε ΦΠΑ) vs 'private'
//   establishmentVerifiedOnIsland— VERIFIED business establishment/branch on that island
//   goodsShippedToIsland         — the goods are actually shipped to that island
//   excludedCategory             — tobacco / means of transport (no reduction)
export function qualifiesForReducedIslandVat({
  islandDestination,
  customerType,
  establishmentVerifiedOnIsland,
  goodsShippedToIsland,
  excludedCategory,
} = {}) {
  return (
    !!islandDestination &&
    customerType === 'business' &&
    !!establishmentVerifiedOnIsland &&
    !!goodsShippedToIsland &&
    !excludedCategory
  );
}
